import {
  buildPermissionBusinessFieldRows,
  QUOTE_SOURCE_FIELDS,
} from './businessFieldDirectory';
import type { FormalActionId } from './formalIntegrationAdapter';
import {
  activeRoleIdForState,
  canUseActivePermissionAction,
  channelPermissionRequirement,
  quotePermissionContext,
  resolveRecursiveEnterpriseFieldGrant,
} from './permissionPolicy';
import type {
  CpqState,
  QuoteFieldPoolRecord,
  ShareFieldCombinationChannel,
  ShareFieldCombinationRecord,
  ShareFieldCombinationSnapshot,
} from './types';

/**
 * Canonical field IDs already consumed by the public quote projection.
 *
 * Quote number is publication metadata, not a selectable field ID. This
 * candidate model intentionally does not introduce a second alias for it.
 */
export const PUBLIC_SHARE_FIELD_IDS = Object.freeze([
  'definition:valid_until',
  'definition:device_name',
  'definition:device_image',
  'definition:quantity',
  'definition:product_final_discount_price',
  'definition:package_item_price',
  'definition:scheme_product_price',
  'definition:scheme_product_subtotal',
  'source:QuoteItem.remark',
] as const);

export type PublicShareFieldId = (typeof PUBLIC_SHARE_FIELD_IDS)[number];

/** Existing canonical sensitive IDs used by negative tests and review. */
export const PUBLIC_SHARE_SENSITIVE_FIELD_IDS = Object.freeze([
  'definition:customer_name',
  'definition:customer_phone',
  'definition:warehouse_stock',
  'definition:device_unit_price',
  'definition:device_untaxed_plan_price',
  'definition:device_compensation_price',
  'definition:purchase_final_price_tax13',
  'definition:dynamic_cost',
  'definition:dynamic_cost_subtotal',
  'definition:device_sku',
  'source:QuoteItem.sourceType',
  'source:QuoteItem.sourceId',
  'source:QuoteItem.sourceName',
  'definition:item_discount',
  'definition:scheme_discount',
  'source:CustomerCatalogRecord.manager',
] as const);

export const FORMAL_QUOTE_EXPORT_ACTION_ID =
  'quotes:quote.export' as const satisfies FormalActionId;

export const PUBLIC_SHARE_COMBINATION_LIFECYCLES = Object.freeze([
  'draft',
  'published',
  'revoked',
] as const);

export const EXPORT_COMBINATION_LIFECYCLES = Object.freeze([
  'draft',
  'ready',
  'disabled',
] as const);

/** The unified output registry used by customer links and all local exports. */
export const SHARE_FIELD_COMBINATION_CHANNELS = Object.freeze([
  'channel:internal-share',
  'channel:public-link',
  'channel:pdf',
  'channel:excel',
  'channel:document',
] as const satisfies readonly ShareFieldCombinationChannel[]);

export const SHARE_FIELD_COMBINATION_CHANNEL_LABELS: Record<
  ShareFieldCombinationChannel,
  string
> = {
  'channel:internal-share': '内部分享',
  'channel:public-link': '客户链接',
  'channel:pdf': 'PDF',
  'channel:excel': 'Excel',
  'channel:document': '报价单 / Word',
};

/** Public-link facts are still governed by the existing projection contract. */
export const SHARE_PUBLIC_REQUIRED_FIELD_IDS = Object.freeze([
  QUOTE_SOURCE_FIELDS.customerName,
  QUOTE_SOURCE_FIELDS.customerPhone,
  QUOTE_SOURCE_FIELDS.validUntil,
  QUOTE_SOURCE_FIELDS.name,
  QUOTE_SOURCE_FIELDS.sku,
  QUOTE_SOURCE_FIELDS.quantity,
  QUOTE_SOURCE_FIELDS.discount,
  QUOTE_SOURCE_FIELDS.lineTotal,
  QUOTE_SOURCE_FIELDS.overallDiscount,
] as const);

/**
 * These two quote-pool members are part of every share/export combination.
 * Their stable IDs are still persisted in the Mock snapshot, but the editor
 * renders them as locked business fields rather than optional candidates.
 */
export const SHARE_FIXED_OUTPUT_FIELD_IDS = Object.freeze([
  QUOTE_SOURCE_FIELDS.image,
  QUOTE_SOURCE_FIELDS.name,
] as const);

/** Public-link header facts are required system output, not selectable pool
 * candidates. They remain in the frozen projection so public-link policy and
 * customer-page rendering keep their existing contract. */
export const SHARE_PUBLIC_REQUIRED_HEADER_FIELD_IDS = Object.freeze([
  QUOTE_SOURCE_FIELDS.customerName,
  QUOTE_SOURCE_FIELDS.customerPhone,
  QUOTE_SOURCE_FIELDS.validUntil,
  QUOTE_SOURCE_FIELDS.overallDiscount,
] as const);

/**
 * A combination stores only the administrator's choice.  Public-link output
 * adds the existing required customer/header/line facts in the shared
 * projection boundary, so those hidden facts never become selectable pool
 * members or persisted user choices.
 */
export function effectiveShareFieldIdsForChannel(
  fieldIds: readonly string[],
  channelId: ShareFieldCombinationChannel,
): string[] {
  if (channelId !== 'channel:public-link') return [...fieldIds];
  return [
    ...new Set([
      ...fieldIds,
      ...(SHARE_PUBLIC_REQUIRED_FIELD_IDS as readonly string[]),
    ]),
  ];
}

const SHARE_OUTPUT_SOURCE_FIELD_IDS = new Set<string>([
  QUOTE_SOURCE_FIELDS.deviceId,
  QUOTE_SOURCE_FIELDS.name,
  QUOTE_SOURCE_FIELDS.sku,
  QUOTE_SOURCE_FIELDS.sourceType,
  QUOTE_SOURCE_FIELDS.sourceId,
  QUOTE_SOURCE_FIELDS.sourceName,
  QUOTE_SOURCE_FIELDS.quantity,
  QUOTE_SOURCE_FIELDS.discount,
  QUOTE_SOURCE_FIELDS.stock,
  QUOTE_SOURCE_FIELDS.replacementDeviceId,
  QUOTE_SOURCE_FIELDS.remark,
  QUOTE_SOURCE_FIELDS.lineTotal,
]);

export function isShareOutputFieldCapable(fieldId: string): boolean {
  return (
    fieldId.startsWith('definition:') ||
    SHARE_OUTPUT_SOURCE_FIELD_IDS.has(fieldId)
  );
}

export interface ShareFieldPoolCandidate {
  readonly fieldId: string;
  readonly label: string;
  readonly poolEntryId: string;
  readonly placement: QuoteFieldPoolRecord['placement'];
  readonly mandatory: boolean;
  readonly fixed: boolean;
}

export interface ShareFieldCombinationUseResult {
  combination: ShareFieldCombinationRecord;
  roleId: string;
  fieldIds: string[];
}

export class ShareFieldCombinationRejectedError extends Error {
  readonly code:
    | 'not_found'
    | 'disabled'
    | 'channel_not_allowed'
    | 'permission_denied'
    | 'field_not_available'
    | 'channel_field_denied'
    | 'public_required_field_missing'
    | 'invalid_configuration';

  constructor(
    code: ShareFieldCombinationRejectedError['code'],
    message: string,
  ) {
    super(message);
    this.name = 'ShareFieldCombinationRejectedError';
    this.code = code;
  }
}

export type PublicShareCombinationLifecycle =
  (typeof PUBLIC_SHARE_COMBINATION_LIFECYCLES)[number];

export type ExportCombinationLifecycle =
  (typeof EXPORT_COMBINATION_LIFECYCLES)[number];

export interface PublicShareFieldCombinationInput {
  readonly kind: 'public-share-field-combination-input';
  readonly id: string;
  readonly lifecycle: PublicShareCombinationLifecycle;
  readonly fieldIds: readonly string[];
}

export interface ExportFieldCombinationInput {
  readonly kind: 'export-field-combination-input';
  readonly id: string;
  readonly lifecycle: ExportCombinationLifecycle;
  readonly fieldIds: readonly string[];
}

declare const publicShareCombinationIdBrand: unique symbol;
declare const exportCombinationIdBrand: unique symbol;
declare const exportFieldIdBrand: unique symbol;

export type PublicShareCombinationId = string & {
  readonly [publicShareCombinationIdBrand]: 'public-share-combination-id';
};

export type ExportCombinationId = string & {
  readonly [exportCombinationIdBrand]: 'export-combination-id';
};

export type ExportFieldId = string & {
  readonly [exportFieldIdBrand]: 'export-field-id';
};

export interface PublicShareFieldCombination {
  readonly kind: 'public-share-field-combination';
  readonly id: PublicShareCombinationId;
  readonly lifecycle: PublicShareCombinationLifecycle;
  readonly fieldIds: readonly PublicShareFieldId[];
}

export interface ExportFieldCombination {
  readonly kind: 'export-field-combination';
  readonly id: ExportCombinationId;
  readonly lifecycle: ExportCombinationLifecycle;
  readonly fieldIds: readonly ExportFieldId[];
}

/**
 * The caller supplies formal permission facts already resolved elsewhere.
 * This type does not accept Disposable permission short names or actor data.
 */
export interface QuoteExportFieldAuthorizationInput {
  readonly allowedActionIds: ReadonlySet<FormalActionId>;
  readonly knownFieldIds: ReadonlySet<string>;
  readonly exportGrantedFieldIds: ReadonlySet<string>;
}

export type FieldCombinationRejectionCode =
  | 'invalid_input'
  | 'invalid_authorization'
  | 'invalid_lifecycle'
  | 'empty_id'
  | 'empty_field_ids'
  | 'duplicate_field_id'
  | 'public_field_id_not_allowed'
  | 'formal_export_action_denied'
  | 'export_field_id_unknown'
  | 'export_field_id_grant_denied';

export class FieldCombinationRejectedError extends Error {
  readonly code: FieldCombinationRejectionCode;

  constructor(code: FieldCombinationRejectionCode, message: string) {
    super(message);
    this.name = 'FieldCombinationRejectedError';
    this.code = code;
  }
}

function reject(code: FieldCombinationRejectionCode, message: string): never {
  throw new FieldCombinationRejectedError(code, message);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStringSet(value: unknown): value is ReadonlySet<string> {
  if (!(value instanceof Set)) return false;
  for (const entry of value) {
    if (typeof entry !== 'string') return false;
  }
  return true;
}

function assertPublicInput(
  input: unknown,
): asserts input is PublicShareFieldCombinationInput {
  if (!isRecord(input) || input.kind !== 'public-share-field-combination-input')
    reject('invalid_input', '公开分享组合输入类型不匹配');
}

function assertExportInput(
  input: unknown,
): asserts input is ExportFieldCombinationInput {
  if (!isRecord(input) || input.kind !== 'export-field-combination-input')
    reject('invalid_input', '导出组合输入类型不匹配');
}

function assertExportAuthorization(
  authorization: unknown,
): asserts authorization is QuoteExportFieldAuthorizationInput {
  if (
    !isRecord(authorization) ||
    !isStringSet(authorization.allowedActionIds) ||
    !isStringSet(authorization.knownFieldIds) ||
    !isStringSet(authorization.exportGrantedFieldIds)
  )
    reject('invalid_authorization', '正式导出授权输入结构不完整');
}

function normalizedId(id: unknown): string {
  if (typeof id !== 'string' || id.trim().length === 0)
    reject('empty_id', '字段组合 ID 不能为空');
  return id.trim();
}

function assertLifecycle(
  lifecycle: unknown,
  allowedLifecycles: readonly string[],
  label: string,
): void {
  if (typeof lifecycle !== 'string' || !allowedLifecycles.includes(lifecycle))
    reject('invalid_lifecycle', `${label}字段组合 lifecycle 不合法`);
}

function normalizedFieldIds(fieldIds: unknown): string[] {
  if (!Array.isArray(fieldIds))
    reject('invalid_input', '字段组合 fieldIds 必须是数组');
  if (fieldIds.length === 0)
    reject('empty_field_ids', '字段组合至少需要一个 fieldId');
  if (fieldIds.some((fieldId) => typeof fieldId !== 'string'))
    reject('invalid_input', '字段组合 fieldId 必须是字符串');
  const normalized = fieldIds.map((fieldId) => fieldId.trim());
  if (normalized.some((fieldId) => fieldId.length === 0))
    reject('invalid_input', '字段组合 fieldId 不能为空');
  if (new Set(normalized).size !== normalized.length)
    reject('duplicate_field_id', '字段组合不能重复选择同一 fieldId');
  return normalized;
}

export function createPublicShareFieldCombination(
  input: unknown,
): PublicShareFieldCombination {
  assertPublicInput(input);
  const id = normalizedId(input.id);
  assertLifecycle(
    input.lifecycle,
    PUBLIC_SHARE_COMBINATION_LIFECYCLES,
    '公开分享',
  );
  const fieldIds = normalizedFieldIds(input.fieldIds);
  const notAllowed = fieldIds.filter(
    (fieldId) =>
      !(PUBLIC_SHARE_FIELD_IDS as readonly string[]).includes(fieldId),
  );
  if (notAllowed.length > 0)
    reject(
      'public_field_id_not_allowed',
      `公开分享组合拒绝 fieldId：${notAllowed.join('、')}`,
    );

  return Object.freeze({
    kind: 'public-share-field-combination' as const,
    id: id as PublicShareCombinationId,
    lifecycle: input.lifecycle,
    fieldIds: Object.freeze(fieldIds) as readonly PublicShareFieldId[],
  });
}

export function createExportFieldCombination(
  input: unknown,
  authorization: unknown,
): ExportFieldCombination {
  assertExportInput(input);
  const id = normalizedId(input.id);
  assertLifecycle(input.lifecycle, EXPORT_COMBINATION_LIFECYCLES, '导出');
  const fieldIds = normalizedFieldIds(input.fieldIds);
  assertExportAuthorization(authorization);

  if (!authorization.allowedActionIds.has(FORMAL_QUOTE_EXPORT_ACTION_ID))
    reject(
      'formal_export_action_denied',
      `缺少正式动作 ${FORMAL_QUOTE_EXPORT_ACTION_ID} 授权`,
    );

  const unknownFieldIds = fieldIds.filter(
    (fieldId) => !authorization.knownFieldIds.has(fieldId),
  );
  if (unknownFieldIds.length > 0)
    reject(
      'export_field_id_unknown',
      `导出组合拒绝未知 fieldId：${unknownFieldIds.join('、')}`,
    );

  const withoutGrant = fieldIds.filter(
    (fieldId) => !authorization.exportGrantedFieldIds.has(fieldId),
  );
  if (withoutGrant.length > 0)
    reject(
      'export_field_id_grant_denied',
      `导出组合缺少字段 export grant：${withoutGrant.join('、')}`,
    );

  return Object.freeze({
    kind: 'export-field-combination' as const,
    id: id as ExportCombinationId,
    lifecycle: input.lifecycle,
    fieldIds: Object.freeze(fieldIds) as readonly ExportFieldId[],
  });
}

function normalizedChannelIds(value: unknown): ShareFieldCombinationChannel[] {
  if (!Array.isArray(value) || value.length === 0)
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '分享字段组合至少需要一个适用渠道',
    );
  const channels = value.map((channel) => String(channel)) as string[];
  if (
    channels.some(
      (channel) =>
        !(SHARE_FIELD_COMBINATION_CHANNELS as readonly string[]).includes(
          channel,
        ),
    ) ||
    new Set(channels).size !== channels.length
  )
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '分享字段组合渠道无效或重复',
    );
  return channels as ShareFieldCombinationChannel[];
}

function outputFieldRows(state: CpqState) {
  return new Map(
    buildPermissionBusinessFieldRows(state.catalog.quoteFieldDefinitions).map(
      (row) => [row.id, row],
    ),
  );
}

function shareFieldPoolCandidateMap(state: CpqState) {
  const rows = outputFieldRows(state);
  const fixed = new Set<string>(SHARE_FIXED_OUTPUT_FIELD_IDS);
  const candidates = new Map<string, ShareFieldPoolCandidate>();
  for (const entry of [...(state.catalog.quoteFieldPool ?? [])].sort(
    (left, right) => left.sortOrder - right.sortOrder,
  )) {
    if (!entry.enabled) continue;
    const fieldId = `definition:${entry.fieldKey}`;
    const row = rows.get(fieldId);
    if (
      !row?.available ||
      row.binding.status === 'unbound' ||
      !entry.displayLabel.trim()
    )
      continue;
    candidates.set(fieldId, {
      fieldId,
      label: entry.displayLabel.trim(),
      poolEntryId: entry.id,
      placement: entry.placement,
      mandatory: entry.mandatory === true,
      fixed: fixed.has(fieldId),
    });
  }
  return candidates;
}

/** The sole candidate source for the share-field editor and model checks. */
export function shareFieldPoolCandidates(
  state: CpqState,
): ShareFieldPoolCandidate[] {
  const candidates = [...shareFieldPoolCandidateMap(state).values()];
  const fixedOrder = new Map<string, number>(
    SHARE_FIXED_OUTPUT_FIELD_IDS.map((fieldId, index) => [fieldId, index]),
  );
  return candidates.sort((left, right) => {
    const leftFixed = fixedOrder.get(left.fieldId);
    const rightFixed = fixedOrder.get(right.fieldId);
    if (leftFixed !== undefined || rightFixed !== undefined)
      return (
        (leftFixed ?? Number.MAX_SAFE_INTEGER) -
        (rightFixed ?? Number.MAX_SAFE_INTEGER)
      );
    return 0;
  });
}

export interface ShareFieldChannelDenial {
  channelId: ShareFieldCombinationChannel;
  reason: string;
}

/**
 * Configuration-time field eligibility uses the same active role, action,
 * field grant and published channel policy as output use-time validation. It
 * intentionally has no quoteId: the editor can explain channel eligibility
 * before a quote is selected, while the use-time assertion still rechecks
 * recursive dependencies against the bound quote.
 */
export function shareFieldChannelDenials(
  state: CpqState,
  fieldId: string,
  channels: readonly ShareFieldCombinationChannel[],
): ShareFieldChannelDenial[] {
  const roleId = activeRoleIdForState(state);
  const row = outputFieldRows(state).get(fieldId);
  return channels.flatMap((channelId) => {
    const label = SHARE_FIELD_COMBINATION_CHANNEL_LABELS[channelId];
    const requirement = channelPermissionRequirement(channelId);
    if (!roleId || !requirement)
      return [{ channelId, reason: `${label}通道权限不足` }];
    if (
      !canUseActivePermissionAction(
        state,
        requirement.featureId,
        requirement.actionId,
      )
    )
      return [{ channelId, reason: `当前角色无${label}权限` }];
    const policy =
      state.catalog.permissionGovernance.roles[roleId]?.channels[channelId];
    if (!policy?.enabled) return [{ channelId, reason: `${label}通道未启用` }];
    const grant =
      state.catalog.permissionGovernance.roles[roleId]?.fieldGrants[fieldId];
    if (!row?.available || row.binding.status === 'unbound')
      return [{ channelId, reason: `${label}通道未允许该字段` }];
    if (!grant?.[requirement.grant])
      return [
        {
          channelId,
          reason:
            requirement.grant === 'export'
              ? `当前角色无${label}导出权限`
              : `当前角色无${label}字段查看权限`,
        },
      ];
    if (!policy.fieldIds.includes(fieldId))
      return [{ channelId, reason: `${label}通道未允许该字段` }];
    return [];
  });
}

function assertShareCombinationChannelPermissions(
  state: CpqState,
  item: Pick<ShareFieldCombinationRecord, 'fieldIds' | 'channels'>,
): void {
  for (const fieldId of item.fieldIds) {
    const denial = shareFieldChannelDenials(state, fieldId, item.channels)[0];
    if (denial) {
      const label = SHARE_FIELD_COMBINATION_CHANNEL_LABELS[denial.channelId];
      throw new ShareFieldCombinationRejectedError(
        'channel_field_denied',
        `字段「${outputFieldRows(state).get(fieldId)?.label ?? fieldId}」不可用于${label}：${denial.reason}`,
      );
    }
  }
}

export function shareFieldCombinationFieldsOutsidePool(
  state: CpqState,
  item: Pick<ShareFieldCombinationRecord, 'fieldIds' | 'channels'>,
): string[] {
  const poolIds = new Set([...shareFieldPoolCandidateMap(state).keys()]);
  return item.fieldIds.filter((fieldId) => !poolIds.has(fieldId));
}

function assertShareCombinationConfiguration(
  state: CpqState,
  item: ShareFieldCombinationRecord,
  current?: ShareFieldCombinationRecord,
): void {
  if (
    !item.id.trim() ||
    !Array.isArray(item.fieldIds) ||
    item.fieldIds.length === 0 ||
    new Set(item.fieldIds).size !== item.fieldIds.length ||
    item.fieldIds.some(
      (fieldId) => typeof fieldId !== 'string' || !fieldId.trim(),
    ) ||
    typeof item.enabled !== 'boolean' ||
    !Number.isInteger(item.sortOrder) ||
    item.sortOrder < 1 ||
    typeof item.systemLocked !== 'boolean'
  )
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '分享字段组合配置无效',
    );
  if (!item.name.trim())
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '请输入分享字段组合名称',
    );
  normalizedChannelIds(item.channels);
  const poolCandidates = shareFieldPoolCandidateMap(state);
  if (
    SHARE_FIXED_OUTPUT_FIELD_IDS.some((fieldId) => !poolCandidates.has(fieldId))
  )
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '报价字段池缺少固定分享字段：设备图片、设备名称必须保持启用',
    );
  if (
    item.fieldIds[0] !== SHARE_FIXED_OUTPUT_FIELD_IDS[0] ||
    item.fieldIds[1] !== SHARE_FIXED_OUTPUT_FIELD_IDS[1]
  )
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '设备图片、设备名称必须始终包含并排在输出字段前列',
    );
  const outsidePool = shareFieldCombinationFieldsOutsidePool(state, item);
  if (outsidePool.length > 0)
    throw new ShareFieldCombinationRejectedError(
      'field_not_available',
      '分享字段只能来自已启用的报价字段池，存在未登记候选字段',
    );
  const rows = outputFieldRows(state);
  for (const fieldId of item.fieldIds) {
    const row = rows.get(fieldId);
    if (
      !row?.available ||
      row?.binding.status === 'unbound' ||
      !poolCandidates.has(fieldId)
    )
      throw new ShareFieldCombinationRejectedError(
        'field_not_available',
        `分享字段「${fieldId}」不存在、未启用或尚未绑定`,
      );
  }
  if (current && current.systemLocked !== item.systemLocked)
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '系统分享字段组合的锁定属性不可改变',
    );
  if (!current && item.systemLocked)
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '新建分享字段组合不能伪造系统锁定标记',
    );
}

/** Strict LocalStorage boundary; malformed combinations never get normalized. */
export function validateShareFieldCombinations(
  state: CpqState,
  combinations: unknown,
): ShareFieldCombinationRecord[] {
  if (!Array.isArray(combinations))
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '分享字段组合持久化状态无效',
    );
  const defaults = createShareFieldCombinations();
  const defaultById = new Map(defaults.map((item) => [item.id, item]));
  const currentById = new Map(
    (state.catalog.shareFieldCombinations ?? []).map((item) => [item.id, item]),
  );
  const incomingIds = new Set(
    combinations.map((item) =>
      item && typeof item === 'object' && 'id' in item
        ? String((item as { id?: unknown }).id)
        : '',
    ),
  );
  for (const seed of defaults.filter((item) => item.systemLocked)) {
    if (!incomingIds.has(seed.id))
      throw new ShareFieldCombinationRejectedError(
        'invalid_configuration',
        '系统分享字段组合不可被损坏或删除',
      );
  }
  const validated = combinations.map((item) => {
    if (!item || typeof item !== 'object')
      throw new ShareFieldCombinationRejectedError(
        'invalid_configuration',
        '分享字段组合持久化状态无效',
      );
    const candidate = item as ShareFieldCombinationRecord;
    if (candidate.systemLocked && !defaultById.has(candidate.id))
      throw new ShareFieldCombinationRejectedError(
        'invalid_configuration',
        '自定义分享字段组合不能伪造系统锁定标记',
      );
    assertShareCombinationConfiguration(
      state,
      candidate,
      currentById.get(candidate.id) ?? defaultById.get(candidate.id),
    );
    return {
      ...structuredClone(candidate),
      fieldIds: [...candidate.fieldIds],
      channels: [...candidate.channels],
    };
  });
  if (new Set(validated.map((item) => item.id)).size !== validated.length)
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '分享字段组合标识不能重复',
    );
  return validated.sort((left, right) => left.sortOrder - right.sortOrder);
}

/** Initial single-model output configuration for the Disposable Mock. */
export function createShareFieldCombinations(): ShareFieldCombinationRecord[] {
  const fixedOutput = [...SHARE_FIXED_OUTPUT_FIELD_IDS];
  return [
    {
      id: 'share-combination-customer-standard',
      name: '客户标准报价',
      fieldIds: [...fixedOutput],
      channels: [...SHARE_FIELD_COMBINATION_CHANNELS],
      enabled: true,
      sortOrder: 1,
      systemLocked: true,
    },
    {
      id: 'share-combination-internal-detail',
      name: '内部报价明细',
      fieldIds: [
        ...fixedOutput,
        'definition:device_sku',
        'definition:quantity',
        'definition:item_discount',
        'definition:product_final_discount_price',
      ],
      channels: [
        'channel:internal-share',
        'channel:pdf',
        'channel:excel',
        'channel:document',
      ],
      enabled: true,
      sortOrder: 2,
      systemLocked: true,
    },
  ];
}

export function saveShareFieldCombinations(
  state: CpqState,
  combinations: ShareFieldCombinationRecord[],
): CpqState {
  if (
    !canUseActivePermissionAction(
      state,
      'permissions',
      'permissions:identity.role.manage',
    )
  )
    throw new Error('仅超级管理员可治理分享字段组合');
  if (!Array.isArray(combinations))
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '分享字段组合必须是数组',
    );
  const current = state.catalog.shareFieldCombinations ?? [];
  const currentById = new Map(current.map((item) => [item.id, item]));
  const ids = new Set<string>();
  const incomingIds = new Set(combinations.map((item) => item.id));
  if (current.some((item) => item.systemLocked && !incomingIds.has(item.id)))
    throw new ShareFieldCombinationRejectedError(
      'invalid_configuration',
      '系统分享字段组合不可删除',
    );
  for (const item of combinations) {
    if (ids.has(item.id))
      throw new ShareFieldCombinationRejectedError(
        'invalid_configuration',
        '分享字段组合标识不能重复',
      );
    assertShareCombinationConfiguration(state, item, currentById.get(item.id));
    assertShareCombinationChannelPermissions(state, item);
    ids.add(item.id);
  }
  const validated = validateShareFieldCombinations(state, combinations);
  const next = structuredClone(state);
  next.catalog.shareFieldCombinations = [...validated].map((item, index) => ({
    ...structuredClone(item),
    id: item.id.trim(),
    name: item.name.trim(),
    fieldIds: [...item.fieldIds],
    channels: [...item.channels],
    sortOrder: index + 1,
  }));
  return next;
}

export function shareFieldCombinationForChannel(
  state: CpqState,
  channelId: ShareFieldCombinationChannel,
): ShareFieldCombinationRecord | undefined {
  return [...(state.catalog.shareFieldCombinations ?? [])]
    .filter((item) => item.enabled && item.channels.includes(channelId))
    .sort((left, right) => left.sortOrder - right.sortOrder)[0];
}

/**
 * Strictly checks the selected combination at the moment of use. This is
 * intentionally separate from the UI's visible-field calculation: a visible
 * field is not proof of a usable output grant.
 */
export function assertShareFieldCombinationUsable(
  state: CpqState,
  quoteId: string,
  combinationId: string,
  channelId: ShareFieldCombinationChannel,
): ShareFieldCombinationUseResult {
  const quote = state.quotes[quoteId];
  if (!quote)
    throw new ShareFieldCombinationRejectedError(
      'not_found',
      '报价不存在，未使用分享字段组合',
    );
  const combination = (state.catalog.shareFieldCombinations ?? []).find(
    (item) => item.id === combinationId,
  );
  if (!combination)
    throw new ShareFieldCombinationRejectedError(
      'not_found',
      '分享字段组合不存在，未使用组合内容',
    );
  if (!combination.enabled)
    throw new ShareFieldCombinationRejectedError(
      'disabled',
      `分享字段组合「${combination.name}」已停用，未使用组合内容`,
    );
  if (!combination.channels.includes(channelId))
    throw new ShareFieldCombinationRejectedError(
      'channel_not_allowed',
      `分享字段组合「${combination.name}」不适用于${SHARE_FIELD_COMBINATION_CHANNEL_LABELS[channelId]}`,
    );
  const roleId = activeRoleIdForState(state);
  const requirement = channelPermissionRequirement(channelId);
  if (!roleId || !requirement)
    throw new ShareFieldCombinationRejectedError(
      'permission_denied',
      '当前成员或输出通道权限不足，未使用分享字段组合',
    );
  if (
    !canUseActivePermissionAction(
      state,
      requirement.featureId,
      requirement.actionId,
    )
  )
    throw new ShareFieldCombinationRejectedError(
      'permission_denied',
      `当前成员没有${SHARE_FIELD_COMBINATION_CHANNEL_LABELS[channelId]}通道权限`,
    );
  const publishedChannel =
    state.catalog.permissionGovernance.roles[roleId]?.channels[channelId];
  if (!publishedChannel?.enabled)
    throw new ShareFieldCombinationRejectedError(
      'permission_denied',
      `${SHARE_FIELD_COMBINATION_CHANNEL_LABELS[channelId]}通道未启用`,
    );
  const rows = outputFieldRows(state);
  const effectiveFieldIds = effectiveShareFieldIdsForChannel(
    combination.fieldIds,
    channelId,
  );
  const allowedFieldIds = new Set(effectiveFieldIds);
  const context = quotePermissionContext(state, quote);
  for (const fieldId of effectiveFieldIds) {
    const row = rows.get(fieldId);
    if (
      !row?.available ||
      row?.binding.status === 'unbound' ||
      !publishedChannel.fieldIds.includes(fieldId)
    ) {
      const label = row?.label ?? fieldId;
      throw new ShareFieldCombinationRejectedError(
        'channel_field_denied',
        `「${SHARE_FIELD_COMBINATION_CHANNEL_LABELS[channelId]}」通道未允许「${label}」字段`,
      );
    }
    if (
      !resolveRecursiveEnterpriseFieldGrant(
        state,
        roleId,
        fieldId,
        requirement.grant,
        quote.status === 'draft' ? 'draft' : 'submitted',
        context,
        allowedFieldIds,
      )
    )
      throw new ShareFieldCombinationRejectedError(
        'permission_denied',
        `您没有「${row.label}」字段的权限`,
      );
  }
  return {
    combination: structuredClone(combination),
    roleId,
    fieldIds: [...combination.fieldIds],
  };
}

export function availableShareFieldCombinationsForChannel(
  state: CpqState,
  quoteId: string,
  channelId: ShareFieldCombinationChannel,
): ShareFieldCombinationRecord[] {
  return configuredShareFieldCombinationsForChannel(state, channelId).filter(
    (item) => {
      try {
        assertShareFieldCombinationUsable(state, quoteId, item.id, channelId);
        return true;
      } catch {
        return false;
      }
    },
  );
}

/**
 * Returns the configured directory candidates without evaluating member field
 * grants. Entry points must use this list so a denied combination remains
 * selectable and can explain its exact failure after selection.
 */
export function configuredShareFieldCombinationsForChannel(
  state: CpqState,
  channelId: ShareFieldCombinationChannel,
): ShareFieldCombinationRecord[] {
  return [...(state.catalog.shareFieldCombinations ?? [])]
    .filter((item) => item.enabled && item.channels.includes(channelId))
    .sort((left, right) => left.sortOrder - right.sortOrder);
}

export interface ShareFieldCombinationAvailabilityDiagnostic {
  combination: ShareFieldCombinationRecord;
  reason: string;
}

/**
 * Keeps configured combinations auditable when channel projection filters them
 * from a selector. Callers must already have passed the relevant action gate
 * before rendering these field-level reasons to a member.
 */
export function shareFieldCombinationAvailabilityDiagnosticsForChannel(
  state: CpqState,
  quoteId: string,
  channelId: ShareFieldCombinationChannel,
): ShareFieldCombinationAvailabilityDiagnostic[] {
  return [...(state.catalog.shareFieldCombinations ?? [])]
    .filter((item) => item.enabled && item.channels.includes(channelId))
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .flatMap((combination) => {
      try {
        assertShareFieldCombinationUsable(
          state,
          quoteId,
          combination.id,
          channelId,
        );
        return [];
      } catch (error) {
        return [
          {
            combination,
            reason:
              error instanceof ShareFieldCombinationRejectedError
                ? error.message
                : '当前成员无法使用该分享字段组合',
          },
        ];
      }
    });
}

/**
 * Explains why a public-link action is unavailable without exposing field
 * names to a role that cannot use the external-share action. The detailed
 * field/channel error is only evaluated after the action itself is allowed.
 */
export function publicShareCombinationAvailabilityReason(
  state: CpqState,
  quoteId: string,
): string | undefined {
  const channelId = 'channel:public-link' as const;
  const requirement = channelPermissionRequirement(channelId);
  const roleId = activeRoleIdForState(state);
  if (
    !roleId ||
    !requirement ||
    !canUseActivePermissionAction(
      state,
      requirement.featureId,
      requirement.actionId,
    )
  )
    return '您没有客户链接通道权限';
  const publishedChannel =
    state.catalog.permissionGovernance.roles[roleId]?.channels[channelId];
  if (!publishedChannel?.enabled) return '客户链接通道未启用';
  const enabledCandidates = (state.catalog.shareFieldCombinations ?? [])
    .filter((item) => item.enabled && item.channels.includes(channelId))
    .sort((left, right) => left.sortOrder - right.sortOrder);
  if (enabledCandidates.length === 0) return '没有启用的客户链接分享字段组合';
  for (const candidate of enabledCandidates) {
    try {
      assertShareFieldCombinationUsable(
        state,
        quoteId,
        candidate.id,
        channelId,
      );
      return undefined;
    } catch (error) {
      if (error instanceof ShareFieldCombinationRejectedError)
        return error.message;
    }
  }
  return '客户链接通道或字段权限不足';
}

export function isValidShareFieldCombinationSnapshot(
  value: unknown,
): value is ShareFieldCombinationSnapshot {
  if (!isRecord(value)) return false;
  const snapshot = value as Partial<ShareFieldCombinationSnapshot>;
  const fieldIds = snapshot.fieldIds;
  const selectedFieldIds = snapshot.selectedFieldIds;
  const fields = snapshot.fields;
  if (!Array.isArray(fieldIds) || !Array.isArray(fields)) return false;
  return (
    typeof snapshot.id === 'string' &&
    snapshot.id.trim().length > 0 &&
    typeof snapshot.name === 'string' &&
    snapshot.name.trim().length > 0 &&
    fieldIds.length > 0 &&
    new Set(fieldIds).size === fieldIds.length &&
    fieldIds.every(
      (fieldId) => typeof fieldId === 'string' && fieldId.trim().length > 0,
    ) &&
    Array.isArray(snapshot.channels) &&
    snapshot.channels.length > 0 &&
    new Set(snapshot.channels).size === snapshot.channels.length &&
    snapshot.channels.every((channel) =>
      (SHARE_FIELD_COMBINATION_CHANNELS as readonly string[]).includes(channel),
    ) &&
    fields.length === fieldIds.length &&
    (selectedFieldIds === undefined ||
      (Array.isArray(selectedFieldIds) &&
        selectedFieldIds.length > 0 &&
        new Set(selectedFieldIds).size === selectedFieldIds.length &&
        selectedFieldIds.every(
          (fieldId) => typeof fieldId === 'string' && fieldId.trim().length > 0,
        ))) &&
    fields.every(
      (field) =>
        isRecord(field) &&
        typeof field.fieldId === 'string' &&
        field.fieldId.trim().length > 0 &&
        typeof field.label === 'string' &&
        field.label.trim().length > 0 &&
        typeof field.type === 'string' &&
        (field.width === 'normal' || field.width === 'long') &&
        (field.sourceKind === 'system' || field.sourceKind === 'manual'),
    ) &&
    fields.every((field) => fieldIds.includes(field.fieldId))
  );
}

/** Read only the frozen field-combination facts for one published version. */
export function frozenShareFieldCombinationForQuote(
  state: Pick<CpqState, 'publications'>,
  quoteId: string,
  quoteVersion: number,
): ShareFieldCombinationSnapshot | undefined {
  const publications = (state.publications ?? []).filter(
    (publication) =>
      publication.quoteId === quoteId &&
      publication.quoteVersion === quoteVersion,
  );
  if (publications.length === 0) return undefined;
  const snapshots = publications.map(
    (publication) => publication.combinationSnapshot,
  );
  if (
    snapshots.some(
      (snapshot) => !isValidShareFieldCombinationSnapshot(snapshot),
    )
  ) {
    throw new Error('历史发布缺少合法的分享字段组合快照');
  }
  const first = snapshots[0] as ShareFieldCombinationSnapshot;
  if (
    snapshots.some(
      (snapshot) => JSON.stringify(first) !== JSON.stringify(snapshot),
    )
  ) {
    throw new Error('同一报价版本存在冲突的分享字段组合快照');
  }
  return structuredClone(first);
}
